import React, { useMemo } from 'react'
import styled from 'styled-components'
import { IPalletTypeItem } from '../../../modules/API/Defaults/models/pallets'
import { IPalletToChangeItem } from '../../../modules/API/Pallets/models/palletToChange'
import PalletTypeSelector from '../../ui/Selectors/PalletTypeSelector'
import IconTextField from '../../ui/Input/IconTextField'
import { FormField, FormGrid } from '../../ui/FormLayout'
import HashIcon from '../../icons/IconLib/HashIcon'
import ScaleIcon from '../../icons/IconLib/ScaleIcon'
import PackageBoxIcon from '../../icons/IconLib/PackageBoxIcon'
import PalletStackIcon from '../../icons/IconLib/PalletStackIcon'
import TrashIcon from '../../icons/IconLib/TrashIcon'

interface Props extends IPalletToChangeItem {
  palletTypeList: IPalletTypeItem[]
  onChangeType: (index: number, type: number) => void
  onChangeQty: (index: number, qty: number) => void
  onClear: (index: number) => void
  index: number
}

const PalletToChangeItem = ({
  palletTypeList,
  Pallet_Num,
  Pallet_Type,
  Quantity_Pallets,
  Real_Pallete,
  onChangeType,
  onChangeQty,
  index,
  onClear,
  Quantity,
  QuantityUnits,
  Weight,
}: Props) => {
  const palletValue = useMemo(
    () => palletTypeList.find((p) => p.Pallete_Type === Pallet_Type) ?? null,
    [palletTypeList, Pallet_Type]
  )

  const handleTypeChange = (p: IPalletTypeItem | null) => {
    onChangeType(index, p ? p.Pallete_Type : 0)
  }

  const handleQtyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChangeQty(index, Number(e.target.value))
  }

  const handleClear = () => {
    onClear(index)
  }

  return (
    <Card>
      <Header>
        <PalletId>
          <HeaderIcon>
            <PalletStackIcon />
          </HeaderIcon>
          <PalletNumber>#{Pallet_Num}</PalletNumber>
        </PalletId>
        <Chips>
          <Chip>
            <HashIcon />
            <ChipValue>{Quantity}</ChipValue>
          </Chip>
          <Chip>
            <PackageBoxIcon />
            <ChipValue>{QuantityUnits}</ChipValue>
          </Chip>
          <Chip>
            <ScaleIcon />
            <ChipValue>{Weight}</ChipValue>
          </Chip>
        </Chips>
      </Header>

      <FormGrid>
        <FormField label='סוג משטח'>
          <PalletTypeSelector
            palletTypes={palletTypeList}
            value={palletValue}
            onSelect={handleTypeChange}
          />
        </FormField>
        <FormField label='כמות משטחים'>
          <IconTextField
            type='number'
            inputMode='numeric'
            leadingIcon={<HashIcon />}
            value={Quantity_Pallets}
            onChange={handleQtyChange}
            onWheel={(e) => (e.currentTarget as HTMLInputElement).blur()}
          />
        </FormField>
      </FormGrid>

      {Real_Pallete !== 0 && (
        <ClearBtn type='button' onClick={handleClear} aria-label='clear'>
          <TrashIcon />
        </ClearBtn>
      )}
    </Card>
  )
}

export default PalletToChangeItem

const Card = styled.div`
  position: relative;
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding: 0.75rem 0.875rem;
  border-radius: ${({ theme }) => theme.radii.lg};
  border: 1px solid ${({ theme }) => theme.colors.line};
  background: ${({ theme }) => theme.colors.surface};
  direction: rtl;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
`

const PalletId = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
`

const HeaderIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.accentDeep};
  svg {
    width: 1rem;
    height: 1rem;
  }
`

const PalletNumber = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.ink};
`

const Chips = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: ${({ theme }) => theme.radii.sm};
  background: ${({ theme }) => theme.colors.line2};
  color: ${({ theme }) => theme.colors.ink2};
  font-size: ${({ theme }) => theme.text.sm};
  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`

const ChipValue = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.ink};
`

const ClearBtn = styled.button`
  position: absolute;
  top: 0.375rem;
  inset-inline-start: 0.375rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.line};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.ink3};
  cursor: pointer;
  border-radius: 0.5rem;
  transition: all ${({ theme }) => theme.motion.fast} ease;

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.danger};
    color: ${({ theme }) => theme.colors.surface};
    border-color: ${({ theme }) => theme.colors.danger};
  }

  &:active {
    transform: translateY(1px);
  }
`
